import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocalStorage } from 'react-use';
import { Button, Paper } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import invoiceService from '../../services/InvoiceService';
import adminSetupService from '../../services/SetupService';
import useFetchCustomerRefRecords from '../MyTime/hooks/useFetchCustomerRefs';
import useFetchClassRefRecords from '../MyTime/hooks/useFetchClassRefs';
import { useFetchTaxCode } from '../MyTime/hooks/useFetchTaxCode';
import useFetchVendorRecords from '../UserNew/hooks/useFetchVendorRecords';
import { useFetchQboTerms } from '../MyTime/hooks/useFetchQboTerms';
import InvoiceTable from './table/InvoiceTable';
import InvoiceEmailLedger from './InvoiceEmailLedger';
import ImportInvoiceDialog from './import/ImportInvoiceDialog';
import { boxshallProductOption } from './helper';

// Status card colour map
const STATUS_COLORS = {
  RequiresReview: '#ed6c02',
  Approved: '#2e7d32'
};

const STATUS_LABELS = {
  RequiresReview: 'Requires review',
  Approved: 'Approved'
};

const Invoices = () => {
  const [activeView, setActiveView] = useLocalStorage('invoiceWorkspaceView', 'bills');
  const showingEmailActivity = activeView === 'emails';
  const { customerRefs, customerRefLoading } = useFetchCustomerRefRecords();
  const { classRefs, classRefLoading } = useFetchClassRefRecords();
  const { taxCode: taxCodes, taxCodeLoading } = useFetchTaxCode();
  const { vendorList, vendorLoading } = useFetchVendorRecords();
  const { qboTerms, qboTermsLoading } = useFetchQboTerms();
  const [defaultTaxCode, setDefaultTaxCode] = useState(null);
  const [defaultItemAccounts, setDefaultItemAccounts] = useState(null);
  const [createdCustomerRefs, setCreatedCustomerRefs] = useState([]);
  const [createdVendorOptions, setCreatedVendorOptions] = useState([]);
  const [boxshallProducts, setBoxshallProducts] = useState([]);
  const [boxshallProductsLoading, setBoxshallProductsLoading] = useState(true);
  const [boxshallProductsError, setBoxshallProductsError] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const supplierOptions = useMemo(() => {
    // eslint-disable-next-line no-undef
    const optionsById = new Map();
    [...vendorList, ...createdVendorOptions].forEach((option) => {
      optionsById.set(String(option.id || option.value), option);
    });
    return [...optionsById.values()];
  }, [createdVendorOptions, vendorList]);

  const customerOptions = useMemo(() => {
    // eslint-disable-next-line no-undef
    const optionsById = new Map();
    [...customerRefs, ...createdCustomerRefs].forEach((option) => {
      optionsById.set(String(option.value), option);
    });
    return [...optionsById.values()];
  }, [createdCustomerRefs, customerRefs]);

  useEffect(() => {
    let mounted = true;
    const fetchDefaultTaxCode = async () => {
      try {
        const response = await adminSetupService.getAdminSetup();
        if (mounted) {
          const setup = response?.data?.data?.[0];
          setDefaultTaxCode(setup?.newItemDefaultTaxCode || null);
          setDefaultItemAccounts(setup?.newItemDefaultAccounts || null);
        }
      } catch (_error) {
        if (mounted) {
          setDefaultTaxCode(null);
          setDefaultItemAccounts(null);
        }
      }
    };
    fetchDefaultTaxCode();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchBoxshallProducts = async () => {
      try {
        setBoxshallProductsLoading(true);
        setBoxshallProductsError('');
        const response = await invoiceService.getBoxshallProducts({ limit: 50 });
        if (mounted) setBoxshallProducts(response.data.data || []);
      } catch (_error) {
        if (mounted) setBoxshallProductsError('Boxshall products could not be loaded');
      } finally {
        if (mounted) setBoxshallProductsLoading(false);
      }
    };
    fetchBoxshallProducts();
    return () => {
      mounted = false;
    };
  }, []);

  const loadBoxshallProductOptions = useCallback(async (search, _loadedOptions, additional = {}) => {
    const skip = Number(additional.skip) || 0;
    const limit = 50;
    try {
      const response = await invoiceService.getBoxshallProducts({
        search: search || undefined,
        skip,
        limit
      });
      const options = (response.data.data || [])
        .map(boxshallProductOption)
        .filter((option) => option.value && option.label);
      const pagination = response.data.pagination || {};

      return {
        options,
        hasMore: Boolean(pagination.hasMore),
        additional: {
          skip: (Number(pagination.skip) || skip) + (Number(pagination.limit) || limit)
        }
      };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not search Boxshall Products');
      return { options: [], hasMore: false, additional: { skip } };
    }
  }, []);

  const previewBoxshallProduct = useCallback(async (values) => {
    const response = await invoiceService.previewBoxshallProduct(values);
    return response.data.data;
  }, []);

  // Paginated invoices with an optional status filter.
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage('invoiceRowsPerPage', 25);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    RequiresReview: 0,
    Approved: 0
  });

  // ── Active status filter (set by clicking a status card) ───────────────────
  const [activeStatus, setActiveStatus] = useLocalStorage('invoiceApprovalStatusFilter', 'RequiresReview');

  // Status totals are calculated before the API applies the active status.
  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        ...(activeStatus && { approvalStatus: activeStatus }),
        ...(submittedSearch && { search: submittedSearch })
      };
      const response = await invoiceService.getInvoices(params);
      const data = response.data.data;
      setInvoices(data.rows || []);
      setTotal(data.total || 0);
      setStatusCounts(data.approvalStatusCounts || data.statusCounts || { RequiresReview: 0, Approved: 0 });
    } catch (_error) {
      setInvoices([]);
      setTotal(0);
      setStatusCounts({ RequiresReview: 0, Approved: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, submittedSearch, activeStatus]);

  useEffect(() => {
    if (!showingEmailActivity) fetchInvoices();
  }, [fetchInvoices, showingEmailActivity]);

  // Debounce search
  useEffect(() => {
    // eslint-disable-next-line no-undef
    const timeout = window.setTimeout(() => {
      setPage(1);
      setSubmittedSearch(search.trim());
    }, 350);
    // eslint-disable-next-line no-undef
    return () => window.clearTimeout(timeout);
  }, [search]);

  const handleOpenPdf = async (invoice) => {
    try {
      const response = await invoiceService.downloadInvoicePdf(invoice._id);
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(response.data);
      // eslint-disable-next-line no-undef
      window.open(url, '_blank', 'noopener,noreferrer');
      // eslint-disable-next-line no-undef
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_error) {
      toast.error('Could not open the invoice PDF');
    }
  };

  const handleReprocess = async (invoice) => {
    try {
      await invoiceService.reprocessInvoice(invoice._id);
      toast.success('Invoice reprocessed');
      await fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not reprocess invoice');
      await fetchInvoices();
    }
  };

  const handleUpdate = async (invoiceId, changes) => {
    const existingInvoice = invoices.find((invoice) => String(invoice._id) === String(invoiceId));
    // eslint-disable-next-line no-undef
    const updatedDescriptions = new Map();
    (changes.lineItems || []).forEach((lineItem, lineIndex) => {
      const previousLine = existingInvoice?.lineItems?.[lineIndex];
      const qboItemId = lineItem.boxshallProduct?.value;
      const previousQboItemId = previousLine?.boxshallProduct?.value;
      const description = String(lineItem.boxshallDescription || '').trim();
      const previousDescription = String(previousLine?.boxshallDescription || '').trim();
      if (qboItemId && String(qboItemId) === String(previousQboItemId) && description !== previousDescription) {
        updatedDescriptions.set(String(qboItemId), description);
      }
    });

    const response = await invoiceService.updateInvoice(invoiceId, changes);
    const updated = response.data.data;
    if (updatedDescriptions.size) {
      setBoxshallProducts((current) =>
        current.map((product) =>
          updatedDescriptions.has(String(product.Id))
            ? { ...product, Description: updatedDescriptions.get(String(product.Id)) }
            : product
        )
      );
    }
    await fetchInvoices();
    return updated;
  };

  const handleCreate = async (invoice) => {
    const response = await invoiceService.createInvoice(invoice);
    await fetchInvoices();
    return response.data.data;
  };

  const handleUploadManualInvoiceFile = async (invoiceId, file) => {
    const response = await invoiceService.uploadManualInvoiceFile(invoiceId, file);
    await fetchInvoices();
    return response.data.data;
  };

  const handleCreateSupplierVendor = async (invoice, vendorValues) => {
    const invoiceId = invoice.invoiceId || invoice._id;
    const response = await invoiceService.createSupplierVendor(invoiceId, vendorValues);
    const { invoice: updatedInvoice, vendor } = response.data.data;
    const option = {
      id: String(vendor.Id),
      value: vendor.DisplayName,
      label: vendor.DisplayName,
      TermRef: vendor.TermDetails || vendor.TermRef || vendorValues.TermRef || null
    };

    setCreatedVendorOptions((current) => [
      ...current.filter((item) => String(item.id || item.value) !== option.id),
      option
    ]);
    setInvoices((current) => current.map((item) => (item._id === updatedInvoice._id ? updatedInvoice : item)));
    toast.success(`Vendor ${vendor.DisplayName} created and linked`);

    return {
      value: option.id,
      label: option.label,
      TermRef: option.TermRef
    };
  };

  const handleCreateBoxshallProduct = async (invoice, lineIndex, productValues) => {
    const invoiceId = invoice.invoiceId || invoice._id;
    const response = await invoiceService.createBoxshallProduct(invoiceId, lineIndex, productValues);
    const { invoice: updatedInvoice, product, reused } = response.data.data;

    setBoxshallProducts((current) => [...current.filter((item) => String(item.Id) !== String(product.Id)), product]);
    setInvoices((current) => current.map((item) => (item._id === updatedInvoice._id ? updatedInvoice : item)));
    toast.success(
      reused
        ? `Existing Boxshall Product ${product.Name} reused and linked`
        : `Boxshall Product ${product.Name} created and linked`
    );

    return {
      value: String(product._id),
      localId: String(product._id),
      qboId: String(product.Id),
      name: product.Name,
      label: product.Name,
      description: product.Description || ''
    };
  };

  const handleCreateCustomer = async (invoice, lineIndex, customerValues) => {
    const invoiceId = invoice.invoiceId || invoice._id;
    const response = await invoiceService.createCustomer(invoiceId, lineIndex, customerValues);
    const { invoice: updatedInvoice, customer } = response.data.data;
    const reference = {
      value: String(customer.Id),
      name: customer.FullyQualifiedName || customer.DisplayName
    };

    setCreatedCustomerRefs((current) => [
      ...current.filter((item) => String(item.value) !== reference.value),
      reference
    ]);
    setInvoices((current) => current.map((item) => (item._id === updatedInvoice._id ? updatedInvoice : item)));
    toast.success(`Customer ${reference.name} created and linked`);

    return reference;
  };

  const handleApprove = async (invoice) => {
    try {
      await invoiceService.approveInvoice(invoice.invoiceId || invoice._id);
      toast.success('Bill approved');
      await fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not approve bill');
    }
  };

  const handleApproveSelected = async (invoiceIds) => {
    // eslint-disable-next-line no-undef
    const results = await Promise.allSettled(invoiceIds.map((invoiceId) => invoiceService.approveInvoice(invoiceId)));
    const approvedCount = results.filter(({ status }) => status === 'fulfilled').length;
    const failedCount = results.length - approvedCount;

    if (approvedCount) {
      toast.success(`${approvedCount} ${approvedCount === 1 ? 'bill' : 'bills'} approved`);
      await fetchInvoices();
    }
    if (failedCount) {
      const firstFailure = results.find(({ status }) => status === 'rejected');
      toast.error(
        failedCount === results.length
          ? firstFailure?.reason?.response?.data?.message || 'Could not approve the selected bills'
          : `${failedCount} ${failedCount === 1 ? 'bill could' : 'bills could'} not be approved`
      );
    }

    return failedCount === 0;
  };

  const handleDeleteSelected = async (invoiceIds) => {
    // eslint-disable-next-line no-undef
    const results = await Promise.allSettled(invoiceIds.map((invoiceId) => invoiceService.deleteInvoice(invoiceId)));
    const deletedCount = results.filter(({ status }) => status === 'fulfilled').length;
    const failedCount = results.length - deletedCount;

    if (deletedCount) {
      toast.success(`${deletedCount} ${deletedCount === 1 ? 'bill' : 'bills'} deleted`);
      await fetchInvoices();
    }
    if (failedCount) {
      const firstFailure = results.find(({ status }) => status === 'rejected');
      toast.error(
        failedCount === results.length
          ? firstFailure?.reason?.response?.data?.message || 'Could not delete the selected bills'
          : `${failedCount} ${failedCount === 1 ? 'bill could' : 'bills could'} not be deleted`
      );
    }

    return failedCount === 0;
  };

  const handleSubmitSelected = async (invoiceSelections, selectionMode) => {
    try {
      const response = await invoiceService.submitInvoices(invoiceSelections, selectionMode);
      const succeeded = response.data?.data?.succeeded || [];
      const failed = response.data?.data?.failed || [];

      if (succeeded.length) {
        toast.success(`${succeeded.length} ${succeeded.length === 1 ? 'bill' : 'bills'} submitted`);
      }
      const purchaseTransferFailure = failed.find(({ code }) => code === 'PURCHASE_TRANSFER_FAILED');
      if (failed.length) {
        toast.error(
          purchaseTransferFailure
            ? `QuickBooks bill created. ${purchaseTransferFailure.message}. Hover over the red invoice row for details.`
            : failed.length === invoiceSelections.length
            ? failed[0]?.message || 'Could not submit the selected bills'
            : `${failed.length} ${failed.length === 1 ? 'bill could' : 'bills could'} not be submitted`
        );
      }
      if (succeeded.length || failed.some(({ billCreated }) => billCreated)) await fetchInvoices();

      return failed.length === 0;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not submit the selected bills');
      return false;
    }
  };

  const handleCheckDocNumbers = useCallback(async (checks) => {
    const response = await invoiceService.checkDocNumbers(checks);
    return response.data?.data?.results || [];
  }, []);

  return (
    <div className="tw-space-y-6">
      <div
        style={{
          alignItems: 'flex-start',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          justifyContent: 'space-between'
        }}
      >
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-foreground tw-m-0">Supplier invoices</h1>
          <p style={{ color: '#667085', margin: '5px 0 0', fontSize: '14px' }}>
            {showingEmailActivity
              ? 'Track every Yellow Category email, including documents that could not be extracted.'
              : 'Review, approve and submit successfully extracted supplier bills.'}
          </p>
        </div>
        <Paper
          aria-label="Invoice workspace view"
          elevation={0}
          sx={{ border: '1px solid #dfe3e8', display: 'inline-flex', gap: 0.5, p: 0.5 }}
        >
          <Button
            aria-pressed={!showingEmailActivity}
            onClick={() => setActiveView('bills')}
            size="small"
            startIcon={<ReceiptLongIcon />}
            variant={!showingEmailActivity ? 'contained' : 'text'}
          >
            Extracted bills
          </Button>
          <Button
            aria-pressed={showingEmailActivity}
            onClick={() => setActiveView('emails')}
            size="small"
            startIcon={<MailOutlineIcon />}
            variant={showingEmailActivity ? 'contained' : 'text'}
          >
            Email Ledger
          </Button>
        </Paper>
      </div>

      {/* ── Status count cards ─────────────────────────────────────────────── */}
      {!showingEmailActivity && (
        <React.Fragment>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {Object.keys(STATUS_COLORS).map((statusKey) => (
              <div
                key={statusKey}
                onClick={() => {
                  setPage(1);
                  setActiveStatus(activeStatus === statusKey ? '' : statusKey);
                }}
                style={{
                  flex: '1 1 120px',
                  minWidth: 120,
                  padding: '12px 20px',
                  borderRadius: 8,
                  backgroundColor: activeStatus === statusKey ? '#f2740d' : '#3f51b5',
                  color: 'white',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.15s ease',
                  userSelect: 'none'
                }}
              >
                <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{statusCounts[statusKey]}</div>
                <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{STATUS_LABELS[statusKey]}</div>
              </div>
            ))}
          </div>

          <InvoiceTable
            invoices={invoices}
            customerRefs={customerOptions}
            classRefs={classRefs}
            taxCodes={taxCodes}
            defaultTaxCode={defaultTaxCode}
            defaultItemAccounts={defaultItemAccounts}
            vendorList={supplierOptions}
            vendorLoading={vendorLoading}
            qboTerms={qboTerms}
            qboTermsLoading={qboTermsLoading}
            boxshallProducts={boxshallProducts}
            boxshallProductsLoading={boxshallProductsLoading}
            boxshallProductsError={boxshallProductsError}
            loadBoxshallProductOptions={loadBoxshallProductOptions}
            referencesLoading={customerRefLoading || classRefLoading}
            taxCodeLoading={taxCodeLoading}
            loading={loading}
            activeStatus={activeStatus}
            total={total}
            page={page}
            pageSize={pageSize}
            search={search}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPage(1);
              setPageSize(value);
            }}
            onSearchChange={setSearch}
            onRefresh={fetchInvoices}
            onOpenPdf={handleOpenPdf}
            onReprocess={handleReprocess}
            onApprove={handleApprove}
            onApproveSelected={handleApproveSelected}
            onDeleteSelected={handleDeleteSelected}
            onSubmitSelected={handleSubmitSelected}
            onCheckDocNumbers={handleCheckDocNumbers}
            onCreateSupplierVendor={handleCreateSupplierVendor}
            onCreateCustomer={handleCreateCustomer}
            onCreateBoxshallProduct={handleCreateBoxshallProduct}
            onPreviewBoxshallProduct={previewBoxshallProduct}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onUploadManualInvoiceFile={handleUploadManualInvoiceFile}
            onImport={() => setImportDialogOpen(true)}
          />
        </React.Fragment>
      )}
      {showingEmailActivity && (
        <InvoiceEmailLedger
          onShowInvoices={() => {
            setPage(1);
            setActiveStatus('');
            setActiveView('bills');
          }}
        />
      )}
      <ImportInvoiceDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSaved={fetchInvoices}
        customerRefs={customerOptions}
        classRefs={classRefs}
        taxCodes={taxCodes}
        defaultTaxCode={defaultTaxCode}
        vendorList={supplierOptions}
        vendorLoading={vendorLoading}
        boxshallProducts={boxshallProducts}
        boxshallProductsLoading={boxshallProductsLoading}
        boxshallProductsError={boxshallProductsError}
        loadBoxshallProductOptions={loadBoxshallProductOptions}
        referencesLoading={customerRefLoading || classRefLoading}
        taxCodeLoading={taxCodeLoading}
        onCheckDocNumbers={handleCheckDocNumbers}
      />
    </div>
  );
};

export default Invoices;
